import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import CustomerServices from "../../services/CustomerServices";
import { HollowDotsSpinner } from "react-epic-spinners";
import { SearchIcon } from "@heroicons/react/outline";
import DatePicker from "react-date-picker";
import { convert, formatDate } from "../../utils/convertDate";
import ApproveAccountOpeningModal from "../../components/Modals/Customer/ApproveAccountOpeningModal";
import Paginate from "../../utils/Paginate";

const statusStyles = {
  Initiated: "bg-yellow-100 text-yellow-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

export default function AccountOpeningApprovalsList() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [filters, setFilters] = useState({
    status: "",
  });
  const [selectedApprovalId, setSelectedApprovalId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [adminType, setAdminType] = useState("");

  const pageSize = 10;

  const [formValues, setFormValues] = useState({ from: null, to: null });
  const [fromm, setFromm] = useState("");
  const [too, setToo] = useState("");

  useEffect(() => {
    var details = sessionStorage.getItem("user");
    if (details) {
      var userDetails = JSON.parse(details);
      setAdminType(userDetails?.adminType || "");
    }
    fetchApprovals(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApprovals = async (page = 1, overrides = {}) => {
    const status = overrides.status ?? filters.status;
    const from = overrides.from ?? fromm;
    const to = overrides.to ?? too;
    setLoading(true);
    try {
      const response = await CustomerServices.listAccountOpeningApprovals(
        page,
        pageSize,
        status,
        "",
        from,
        to
      );

      if (response && response.data) {
        setApprovals(response.data.result || []);
        setPagination({
          pageIndex: response.data.pageIndex,
          pageSize: pageSize,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      }
    } catch (error) {
      console.error("Error fetching account opening approvals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (approval) => {
    setSelectedApprovalId(approval.id);
    setModalOpen(true);
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setFilters((prev) => ({ ...prev, status }));
    fetchApprovals(1, { status });
  };

  const dateValidation = {
    fromHandler: (event) => {
      setFormValues({ ...formValues, from: event });
    },
    toHandler: (event) => {
      setFormValues({ ...formValues, to: event });
    },
  };

  const range = async (e) => {
    e.preventDefault();
    const from = formValues.from ? convert(formatDate(formValues.from)) : "";
    const to = formValues.to ? convert(formatDate(formValues.to)) : "";
    setFromm(from);
    setToo(to);
    fetchApprovals(1, { from, to });
  };

  const handlePageChange = (newPage) => {
    fetchApprovals(newPage);
  };

  const handleModalSuccess = () => {
    fetchApprovals(pagination.pageIndex);
  };

  if (adminType?.toLowerCase() !== "checker") {
    return (
      <Layout pageTag="approvals">
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                <svg className="h-8 w-8 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-center text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
              <div className="rounded-md bg-red-50 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">Only Checkers have permission to view account opening approvals.</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-indigo-600 font-medium text-lg">{adminType?.charAt(0).toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">Your current role: <span className="text-indigo-600 font-semibold">{adminType}</span></p>
                    <p className="text-sm text-gray-500 truncate">Required role: Checker</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => (window.location.href = "/dashboard")}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Account Opening Approvals" pageTag="approvals">
      <main>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="py-4">
            {/* Breadcrumb navigation */}
            <div className="mb-4">
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2">
                  <li>
                    <a href="/approvals" className="text-sm text-gray-500 hover:text-gray-700">
                      Approvals
                    </a>
                  </li>
                  <li className="text-sm text-gray-400">/</li>
                  <li className="text-sm text-gray-900 font-medium">Account Opening Approvals</li>
                </ol>
              </nav>
            </div>

            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-semibold text-gray-900">Account Opening Approval Requests</h1>
                <p className="mt-2 text-sm text-gray-700">
                  List of all onboarding re-initiation requests awaiting your approval
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="mt-4 flex flex-wrap gap-4 items-end">
              <div>
                <p className="text-sm leading-3 text-gray-500 mb-1">From</p>
                <div>
                  <DatePicker
                    onChange={dateValidation.fromHandler}
                    value={formValues.from}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm leading-3 text-gray-500 mb-1">To</p>
                <div>
                  <DatePicker
                    onChange={dateValidation.toHandler}
                    value={formValues.to}
                  />
                </div>
              </div>
              <div className="w-36">
                <p className="text-sm leading-3 text-gray-500 mb-1">Status</p>
                <select
                  value={filters.status}
                  onChange={handleStatusChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">All</option>
                  <option value="Initiated">Initiated</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div>
                <button
                  onClick={range}
                  className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
                >
                  <SearchIcon className="h-4 w-4 mr-2" />
                  Search
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="mt-8 flex flex-col">
              <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    {loading ? (
                      <div className="flex justify-center py-12">
                        <HollowDotsSpinner size={50} color="#4D627E" />
                      </div>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                              User ID
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              User Name
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Unique Identifier
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Status
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Initiated By
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Date Initiated
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Reason / Rejection Reason
                            </th>
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6 font-semibold text-gray-900">
                              <span>Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {approvals.map((approval) => (
                            <tr key={approval.id}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {approval.requestData?.userId ?? "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {approval.requestData?.userName || "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {approval.requestData?.uniqueIdentifier || "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${statusStyles[approval.status] || "bg-gray-100 text-gray-700"}`}>
                                  {approval.status}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {approval.initiatedBy || "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {approval.dateInitiated
                                  ? new Date(approval.dateInitiated).toLocaleString()
                                  : "-"}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                                {approval.status === "Rejected"
                                  ? approval.rejectionReason || "-"
                                  : approval.reason || "-"}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                  onClick={() => handleViewDetails(approval)}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  {approval.status === "Initiated" ? "View" : "View Details"}
                                </button>
                              </td>
                            </tr>
                          ))}
                          {approvals.length === 0 && !loading && (
                            <tr>
                              <td colSpan="8" className="text-center py-8 text-gray-500">
                                No account opening approval requests found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {!loading && approvals.length > 0 && (
              <Paginate
                nextPage={pagination.hasNextPage}
                pageIndex={pagination.pageIndex}
                totalCount={pagination.totalCount}
                prevPage={pagination.hasPreviousPage}
                totalPages={pagination.totalPages}
                setPageIndex={handlePageChange}
              />
            )}
          </div>
        </div>
      </main>

      <ApproveAccountOpeningModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        approvalId={selectedApprovalId}
        onSuccess={handleModalSuccess}
      />
    </Layout>
  );
}
